import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import { MapPin, Send } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useInView } from '../hooks/useInView';

const Section = styled.section`
  padding: 120px 0;
  background: #FFFDF7;
  @media (min-width: 768px) { padding: 160px 0; }
`;

const Container = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 32px;
  transition: opacity 1s ease, transform 1s ease;
  ${({ $inView }) =>
    $inView
      ? css`opacity: 1; transform: translateY(0);`
      : css`opacity: 0; transform: translateY(40px);`}
`;

const Grid = styled.div`
  display: grid;
  gap: 72px;
  @media (min-width: 1024px) {
    grid-template-columns: 1fr 1fr;
    align-items: start;
  }
`;

const InfoSide = styled.div``;

const Eyebrow = styled.p`
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: #C07020;
  margin-bottom: 16px;
`;

const Title = styled.h2`
  font-family: 'Cormorant Garamond', serif;
  font-size: clamp(2.4rem, 5vw, 3.8rem);
  color: #1A1208;
  margin-bottom: 12px;
  line-height: 1.05;
  letter-spacing: -0.01em;
`;

const Divider = styled.div`
  width: 48px;
  height: 2px;
  background: #C07020;
  margin-bottom: 24px;
  border-radius: 1px;
`;

const Lead = styled.p`
  font-size: 1rem;
  color: rgba(26,18,8,0.62);
  max-width: 380px;
  line-height: 1.8;
  margin-bottom: 48px;
`;

const ContactList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const ContactRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
`;

const IconCircle = styled.div`
  width: 44px; height: 44px;
  border-radius: 50%;
  background: rgba(124,58,0,0.08);
  border: 1px solid rgba(124,58,0,0.12);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  svg { color: #7C3A00; }
`;

const ContactInfo = styled.div`
  h4 { font-weight: 600; font-size: 0.9rem; color: #1A1208; margin-bottom: 4px; letter-spacing: 0.03em; }
  p { font-size: 0.88rem; color: rgba(26,18,8,0.58); }
  a { color: inherit; transition: color 0.2s; }
  a:hover { color: #7C3A00; }
`;

const Socials = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 40px;
`;

const SocialBtn = styled.a`
  width: 44px; height: 44px;
  border-radius: 50%;
  background: #1A1208;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #FAF3E0;
  transition: background 0.25s, transform 0.2s;
  svg { width: 18px; height: 18px; fill: currentColor; }
  &:hover { background: #7C3A00; transform: translateY(-2px); }
`;

/* Form */
const FormCard = styled.div`
  background: #FAF3E0;
  border-radius: 24px;
  padding: 48px 40px;
  box-shadow: 0 8px 48px rgba(26,18,8,0.07);
  border: 1px solid rgba(232,223,208,0.6);
`;

const FormGroup = styled.div`
  margin-bottom: 24px;
  label {
    display: block;
    font-size: 0.8rem;
    font-weight: 600;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: rgba(26,18,8,0.6);
    margin-bottom: 8px;
  }
`;

const inputStyle = css`
  width: 100%;
  padding: 14px 18px;
  background: #FFFDF7;
  border: 1px solid #E8DFD0;
  border-radius: 10px;
  font-family: 'Jost', sans-serif;
  font-size: 0.95rem;
  color: #1A1208;
  outline: none;
  transition: border-color 0.25s, box-shadow 0.25s;
  &::placeholder { color: rgba(26,18,8,0.3); }
  &:focus {
    border-color: #C07020;
    box-shadow: 0 0 0 3px rgba(192,112,32,0.12);
  }
`;

const Input = styled.input`${inputStyle}`;
const Textarea = styled.textarea`${inputStyle} resize: none;`;

const SubmitBtn = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 16px;
  background: #7C3A00;
  color: #FFFDF7;
  font-family: 'Jost', sans-serif;
  font-weight: 500;
  font-size: 0.9rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  border-radius: 999px;
  transition: background 0.25s, transform 0.2s, box-shadow 0.25s;
  &:hover:not(:disabled) {
    background: #C07020;
    transform: translateY(-1px);
    box-shadow: 0 8px 24px rgba(124,58,0,0.25);
  }
  &:disabled { opacity: 0.65; cursor: not-allowed; }
`;

export function Contact() {
  const { t } = useLanguage();
  const { ref, isInView } = useInView({ threshold: 0.2 });
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    await new Promise((r) => setTimeout(r, 1000));
    setSending(false);
    setSent(true);
    setForm({ name: '', email: '', message: '' });
    setTimeout(() => setSent(false), 3000);
  };

  const update = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  return (
    <Section id="contacto" aria-label="Contacto Hacienda Real">
      <Container ref={ref} $inView={isInView}>
        <Grid>
          <InfoSide>
            <Eyebrow>{t('Contact Us', 'Contáctanos', 'Contactez-nous')}</Eyebrow>
            <Title>{t('Get in Touch', 'Hablemos', 'Parlons')}</Title>
            <Divider />
            <Lead>
              {t(
                'We have offices in the USA and Mexico. Contact us to find your nearest location.',
                'Tenemos oficinas en Estados Unidos y México. Contáctanos para encontrar la ubicación más cercana.',
                'Nous avons des bureaux aux États-Unis et au Mexique. Contactez-nous pour trouver votre emplacement le plus proche.'
              )}
            </Lead>
            <ContactList>
              <ContactRow>
                <IconCircle><MapPin size={20} /></IconCircle>
                <ContactInfo>
                  <h4>USA</h4>
                  <p>Phone: <a href="tel:+16828129717">682 812 9717</a></p>
                </ContactInfo>
              </ContactRow>
              <ContactRow>
                <IconCircle><MapPin size={20} /></IconCircle>
                <ContactInfo>
                  <h4>México</h4>
                  <p>Tel: <a href="tel:+523957884644">395 788 4644</a></p>
                </ContactInfo>
              </ContactRow>
            </ContactList>

            <Socials>
              <SocialBtn href="https://wa.me/" target="_blank" rel="noopener noreferrer" aria-label="WhatsApp">
                <svg viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
              </SocialBtn>
              <SocialBtn href="https://www.instagram.com/cajetahaciendareal?igsh=b2lpM3FpZGZsNjZ6" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                <svg viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
              </SocialBtn>
              <SocialBtn href="https://www.tiktok.com/@cajetahaciendarea?_r=1&_t=ZS-95DUF6gHRAa" target="_blank" rel="noopener noreferrer" aria-label="TikTok">
                <svg viewBox="0 0 24 24"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/></svg>
              </SocialBtn>
            </Socials>
          </InfoSide>

          <FormCard>
            <form onSubmit={handleSubmit}>
              <FormGroup>
                <label htmlFor="name">{t('Name', 'Nombre', 'Nom')}</label>
                <Input
                  type="text"
                  id="name"
                  required
                  value={form.name}
                  onChange={update('name')}
                  placeholder={t('Your name', 'Tu nombre', 'Votre nom')}
                />
              </FormGroup>
              <FormGroup>
                <label htmlFor="email">{t('Email', 'Correo', 'E-mail')}</label>
                <Input
                  type="email"
                  id="email"
                  required
                  value={form.email}
                  onChange={update('email')}
                  placeholder={t('your@email.com', 'tu@correo.com', 'votre@email.com')}
                />
              </FormGroup>
              <FormGroup>
                <label htmlFor="message">{t('Message', 'Mensaje', 'Message')}</label>
                <Textarea
                  id="message"
                  required
                  rows={5}
                  value={form.message}
                  onChange={update('message')}
                  placeholder={t('How can we help?', '¿Cómo podemos ayudarte?', 'Comment pouvons-nous vous aider ?')}
                />
              </FormGroup>
              <SubmitBtn type="submit" disabled={sending}>
                {sending ? t('Sending...', 'Enviando...', 'Envoi...') : sent ? t('Sent! ✓', '¡Enviado! ✓', 'Envoyé ! ✓') : (
                  <>{t('Send Message', 'Enviar Mensaje', 'Envoyer')}<Send size={18} /></>
                )}
              </SubmitBtn>
            </form>
          </FormCard>
        </Grid>
      </Container>
    </Section>
  );
}
